import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, CSSProperties, StyleDeclarationValue} from 'aphrodite';
import classNames from 'classnames';

import {multiLineEllipsisText} from 'styles/Utilities';

export const COMMON_NODE_WIDTH = 200;
const BORDER_RADIUS = 4;
const HANDLE_DOT_DIAMETER = 10;
const HANDLE_CONNECTION_DIAMETER = 20;
const NODE_HANDLE_PADDING_FROM_NODE = 8;

export const FONT_SIZE = {
  XSMALL: '10px',
  SMALL: '11px',
  MEDIUM: '12px',
  LARGE: '13px',
};

export const FONT_WEIGHT: Record<string, CSSProperties['fontWeight']> = {
  NORMAL: 400,
  SEMIBOLD: 600,
};

export const SPACING = {
  XSMALL: '2px',
  SMALL: '4px',
  MEDIUM: '6px',
  MEDIUM_LARGE: '8px',
  LARGE: '16px',
};

export const ICON_SIZE = {
  SMALL: '10px',
  MEDIUM: '16px',
};

const BORDER = {
  THIN_LIGHT: `1px solid ${Color.GRAY_200}`,
  THIN_GRAY_MEDIUM: `1px solid ${Color.GRAY_300}`,
  THIN_GRAY_MEDIUM_DARK: `1px solid ${Color.GRAY_500}`,
  THIN_GRAY_DARK: `1px solid ${Color.GRAY_700}`,
  NONE: 'none',
};

const BORDER_RADII = {
  TOP_CURVE: `${BORDER_RADIUS}px ${BORDER_RADIUS}px 0px 0px`,
  BOTTOM_CURVE: `0px 0px ${BORDER_RADIUS}px ${BORDER_RADIUS}px`,
  ALL_CURVE: `${BORDER_RADIUS}px`,
  ALL_CURVE_WITH_BORDER: `${BORDER_RADIUS + 1}px`,
};

const STATUS_COLORS = {
  DISABLED: Color.GRAY_50,
  ERROR: Color.RED_500,
  IN_PROGRESS: Color.BLUE_500,
  PENDING: Color.GRAY_600,
  SUCCESS: Color.GREEN_600,
  WARNING: Color.ORANGE_500,
};

export const NODE_HEADER_COLORS = {
  ERROR: Color.RED_100,
  IN_PROGRESS: Color.TEAL_100,
  PENDING: Color.GRAY_100,
  SUCCESS: Color.GREEN_100,
  WARNING: Color.ORANGE_100,
};

export const NODE_STYLES = StyleSheet.create({
  disabled: {
    background: STATUS_COLORS.DISABLED,
  },
  nodeSelected: {
    border: `1px solid ${Color.BLUE_450}`,
    ':active': {
      border: `1px solid ${Color.BLUE_450}`,
    },
  },
  nodeDragging: {
    border: BORDER.THIN_GRAY_DARK,
    boxShadow: '0px 2px 6px 0px rgba(186, 186, 189, 0.75)',
  },
  nodeHovered: {
    boxShadow: '0px 2px 6px 0px rgba(186, 186, 189, 0.75)',
  },
  hoverBorder: {
    border: BORDER.THIN_GRAY_MEDIUM_DARK,
    ':active': {
      border: BORDER.THIN_GRAY_DARK,
    },
  },
});

export const NODE_STATUS_STYLES = StyleSheet.create({
  warningBorder: {
    borderColor: STATUS_COLORS.WARNING,
    ':active': {
      border: BORDER.THIN_GRAY_DARK,
    },
  },
  warningIcon: {
    color: STATUS_COLORS.WARNING,
  },
  errorBorder: {
    borderColor: STATUS_COLORS.ERROR,
    ':active': {
      border: BORDER.THIN_GRAY_DARK,
    },
  },
  errorIcon: {
    color: STATUS_COLORS.ERROR,
  },
  inProgressIcon: {
    color: STATUS_COLORS.IN_PROGRESS,
  },
  pendingIcon: {
    color: STATUS_COLORS.PENDING,
  },
  successIcon: {
    color: STATUS_COLORS.SUCCESS,
  },
});

export const NODE_WRAPPER_STYLES = StyleSheet.create({
  benchlingNodeWrapper: {
    width: `${COMMON_NODE_WIDTH}px`,
    borderRadius: BORDER_RADII.ALL_CURVE_WITH_BORDER,
    userSelect: 'none',
  },
  benchlingNode: {
    border: BORDER.THIN_LIGHT,
    borderRadius: BORDER_RADII.ALL_CURVE_WITH_BORDER,
    background: Color.WHITE,
  },
});

export const NODE_HEADER_STYLES = StyleSheet.create({
  nodeHeader: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: `${SPACING.MEDIUM_LARGE} ${SPACING.MEDIUM_LARGE}`,
    position: 'relative',
    overflow: 'visible',
  },
  nodeHeaderWithContentBelow: {
    borderRadius: BORDER_RADII.TOP_CURVE,
  },
  nodeHeaderWithNoContentBelow: {
    borderRadius: BORDER_RADII.ALL_CURVE,
  },
  interactiveNodeHeader: {
    ':hover': {
      color: Color.GRAY_900,
      cursor: 'grab',
    },
    ':active': {
      cursor: 'grabbing',
    },
  },
  nodeHeaderBorder: {
    borderBottom: BORDER.THIN_GRAY_MEDIUM,
  },
  nodeHeaderContent: {
    gap: SPACING.MEDIUM,
  },
  nodeHeaderTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: SPACING.XSMALL,
  },
  nodeHeaderTopRowLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: SPACING.MEDIUM_LARGE,
    flex: '1 1 auto',
    padding: '0px, 2px, 0px, 2px',
    minWidth: '0px',
    paddingRight: SPACING.XSMALL,
  },
  nodeHeaderTopRowIcon: {
    minHeight: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.SMALL,
    color: Color.GRAY_500,
    flex: '0 0 auto',
  },
  nodeName: {
    fontWeight: FONT_WEIGHT.SEMIBOLD,
    color: Color.GRAY_900,
    fontSize: FONT_SIZE.LARGE,
    flex: '1 1 auto',
    ...multiLineEllipsisText(2),
  },
  nodeIcon: {
    width: ICON_SIZE.MEDIUM,
    height: ICON_SIZE.MEDIUM,
  },
  statusIcon: {
    width: ICON_SIZE.MEDIUM,
    height: ICON_SIZE.MEDIUM,
  },
  secondaryInfo: {
    color: Color.GRAY_600,
    ...multiLineEllipsisText(2),
  },
});

export const NODE_HEADER_HANDLE_STYLES = StyleSheet.create({
  nodeHandle: {
    background: 'transparent',
    border: 'none',
    borderRadius: HANDLE_CONNECTION_DIAMETER / 2,
    height: HANDLE_CONNECTION_DIAMETER,
    width: HANDLE_CONNECTION_DIAMETER,
  },
  leftHandle: {
    left: `-${HANDLE_CONNECTION_DIAMETER / 2 + NODE_HANDLE_PADDING_FROM_NODE}px`,
  },
  rightHandle: {
    right: `-${HANDLE_CONNECTION_DIAMETER / 2 + NODE_HANDLE_PADDING_FROM_NODE}px`,
  },
  topHandle: {
    top: '0px',
  },
  bottomHandle: {
    bottom: '0px',
  },
  handleDot: {
    border: `solid 1px ${Color.GRAY_500}`,
    width: HANDLE_DOT_DIAMETER,
    height: HANDLE_DOT_DIAMETER,
    borderRadius: `${HANDLE_DOT_DIAMETER / 2}px`,
    margin: (HANDLE_CONNECTION_DIAMETER - HANDLE_DOT_DIAMETER) / 2,
    background: Color.WHITE,
    pointerEvents: 'none',
  },
  hidden: {
    visibility: 'hidden',
  },
  lockedNodeHandle: {
    pointerEvents: 'none',
  },
});

export type NodeHeaderEdgeHandleStyle = StyleDeclarationValue;

export const NODE_SECTION_SUMMARIES_STYLES = StyleSheet.create({
  sectionSummaries: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: `${SPACING.XSMALL} ${SPACING.MEDIUM_LARGE}`,
    background: Color.GRAY_50,
    gap: SPACING.LARGE,
    pointerEvents: 'all',
    cursor: 'default',
  },
  sectionSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.SMALL,
  },
  sectionSummaryIcon: {
    width: ICON_SIZE.SMALL,
    height: ICON_SIZE.SMALL,
  },
  sectionSummaryCount: {
    fontSize: FONT_SIZE.MEDIUM,
    fontWeight: FONT_WEIGHT.NORMAL,
    color: Color.GRAY_600,
  },
  sectionSummariesBorder: {
    borderRadius: BORDER_RADII.BOTTOM_CURVE,
  },
});

export const NODE_SECTIONS_STYLES = StyleSheet.create({
  sections: {
    pointerEvents: 'all',
    cursor: 'default',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
  },
  sectionBorder: {
    borderTop: BORDER.THIN_GRAY_MEDIUM,
  },
  sectionVisible: {
    padding: `${SPACING.SMALL} 0px`,
    gap: SPACING.XSMALL,
  },
  sectionName: {
    color: Color.GRAY_900,
    fontSize: FONT_SIZE.SMALL,
    fontWeight: FONT_WEIGHT.SEMIBOLD,
    padding: `0 ${SPACING.MEDIUM_LARGE}`,
  },
  sectionRows: {
    display: 'flex',
    flexDirection: 'column',
  },
});

export const NODE_SECTION_ROW_STYLES = StyleSheet.create({
  sectionRow: {
    display: 'flex',
    padding: `${SPACING.XSMALL} ${SPACING.MEDIUM_LARGE}`,
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
  },
  rowPrimaryColumn: {
    color: Color.GRAY_900,
    fontSize: FONT_SIZE.SMALL,
    fontWeight: FONT_WEIGHT.NORMAL,
  },
  rowSecondaryColumn: {
    color: Color.GRAY_600,
    fontSize: FONT_SIZE.SMALL,
    fontWeight: FONT_WEIGHT.NORMAL,
  },
});

export const ROW_HANDLE_STYLES = StyleSheet.create({
  rowHandle: {
    background: 'transparent',
    border: 'none',
    borderRadius: HANDLE_CONNECTION_DIAMETER / 2,
    height: HANDLE_CONNECTION_DIAMETER,
    width: HANDLE_CONNECTION_DIAMETER,
  },
  leftHandle: {
    left: `-${HANDLE_CONNECTION_DIAMETER + 2.5}px`,
  },
  rightHandle: {
    right: `-${HANDLE_CONNECTION_DIAMETER + 2.5}px`,
  },
  handleDot: {
    width: HANDLE_DOT_DIAMETER,
    height: HANDLE_DOT_DIAMETER,
    borderRadius: `${HANDLE_DOT_DIAMETER / 2}px`,
    margin: (HANDLE_CONNECTION_DIAMETER - HANDLE_DOT_DIAMETER) / 2,
    background: Color.WHITE,
    border: `solid 1px ${Color.GRAY_500}`,
    pointerEvents: 'none',
  },
  hidden: {
    visibility: 'hidden',
  },
  lockedRowHandle: {
    cursor: 'pointer',
    pointerEvents: 'none',
  },
});

/**
 * Reactflow relies on 'nodrag' class to prevent elements from dragging a node.
 * Use for any elements within the method that should not be draggable.
 *
 * Ref: https://reactflow.dev/docs/api/nodes/custom-nodes/#prevent-dragging--selecting
 */
export function noDragClass(otherClasses?: string): string {
  return classNames('nodrag', otherClasses);
}
